import _ from 'lodash';
import updater from './updater';
import cognitiveArrays from 'view/insertion/cognitive-arrays';
import ssmlHelper from 'view/insertion/ssml-helper';	
import CreativeContextMenuMixin from './CreativeContextMenu.vue';
import EasyCreativeHelper from './EasyCreative/helper.js';
import undoRedoMixin from 'view/audioTracker/audioTrackerUndoRedo';
import creativeMixin from './detail-creative.js';
import editingLockMixin from './detail-editing-lock.js';
import insertionMixin from './detail-insertion.js';
import insertionActionsMixin from './detail-insertion-actions.js';
import publicationMixin from './detail-publication.js';
import wapiPluginsMixin from './detail-wapi-plugins.js';
import rulesMixin from './detail-rules.js';
import voicesMixin from './detail-voices.js';
import tracksMixin from './detail-tracks.js';
import networkStudioMixin from './detail-network-studio.js';
import sonicScribeMixin from './sonic-scribe.js';
import detailFileUploadRequestMixin from './detail-file-upload-request.js';

export default {
	translationCategory: 'creative.Detail',
	name: 'creative-detail',
	components: {
	},
	props: ['creativeId', 'isNew', 'disabled', 'opts'],
	mixins: [
		updater, cognitiveArrays, undoRedoMixin, CreativeContextMenuMixin, creativeMixin, editingLockMixin, insertionMixin, publicationMixin, wapiPluginsMixin,
		rulesMixin, voicesMixin, tracksMixin, networkStudioMixin, insertionActionsMixin, sonicScribeMixin, detailFileUploadRequestMixin
	],
	data() {
		return {
			creativeValidationStatus: ['None', 'RequestsAreSending', 'AllRequestsSentSuccessfully', 'NotAllRequestsAreSent'],
			sendingValidationRequest: false,
			pressedKeys: []
		};
	},
	computed: {
		readonly() {
			return this.publicationInProgress || !this.dataIsLoadedFirst || this.creative?.IsPublished || this.creativeIsLockedByOtherUser || !!this.userRestrictedAccess;
		},
		userRestrictedAccess() {
			const accessObject = {
				aggregatedTracks: []
			};

			const voiceoverItem = this.creative?.JsonConfig?.voiceoverRequests?.find(f => f.voiceoverRequestId === this.$route.query?.voiceoverRequestId);
			if (voiceoverItem && !voiceoverItem.completed) {
				accessObject.aggregatedTracks.push(...(voiceoverItem?.voiceoverTrackNumbers || []));
			}

			accessObject.isVoiceover = !!voiceoverItem && !voiceoverItem?.completed;
			accessObject.voiceoverRequestId = voiceoverItem?.voiceoverRequestId;

			return this.$auth.isAnonym() && accessObject || null;
		},
		isVoiceover() {
			return this.userRestrictedAccess?.isVoiceover;
		},
		validationDueDate: {
			get() {
				return this.creative?.ValidationDueDate;
			},
			set(v) {
				this.creative.ValidationDueDate = v;
			}
		},
		validationStatus: {
			get() {
				return this.creative?.ValidationStatus;
			},
			set(v) {
				this.creative.ValidationStatus = v;
			}
		},
		sendValidationRequestsButtonEnabled() {
			return this.creativeValidationStatus[this.validationStatus] !== 'RequestsAreSending'
				&& this.creativeValidationStatus[this.validationStatus] !== 'AllRequestsSentSuccessfully';
		},
	},
	watch: {
		loadedTracks() {
			this.calcVisibleTracks();
		},
		simulateRules() {
			this.checkSimulateRulesAllTracks();
		},
		dataIsLoaded(v) {
			if (__DEBUG__)
				console.debug('Data is loaded changed', this.dataIsLoaded);
			if (v) {
				this.dataIsLoadedFirst = true;
				this.checkDefaultReplacedVoices();
			}
			this.$emit('loaded', !!v);
		},
		loadingItems() {
			this.calcDataIsLoaded();
		},
		isLoading(v) {
			if (__DEBUG__)
				console.debug('Is loading changed', v);
			if (v < 0)
				this.isLoading = 0;
			this.calcDataIsLoaded();
		},
		
		creativeId() {
			this.initCreative();
		},
		'creative.JsonConfig': {
			handler() {
				if (this.creative?.__addDataToUndoHistory)
					this.creative?.__addDataToUndoHistory();
				this.$updateCreativeJson();
			},
			deep: true
		},
		'creative.Name'(v) {
			this.$setTitle(v);
		},
		totalDefaultVoiceReplaced(v) {
			if (!v) {
				if (this.isMixing) this.mixAndDownloadFinalAudio();
				if (this.$totalDefaultVoiceReplacedLast) {
					this.$notifySuccess('Premium voices successfully activated');
					this.$emit('default-voices-replaced', v);
					delete this.$totalDefaultVoiceReplacedLast;
				}
			} else {
				this.$totalDefaultVoiceReplacedLast = v;
				this.$emit('default-voices-replacing', v);
			}
		}
	},
	mounted() {
		if (__DEBUG__) {
			window.creativeDetail = this;
			window.ssmlHelperInst = ssmlHelper;
		}
		window.creative = {
			onPublish: this.onPublish,
			publish: () => this.publishFinalAudio(true)
		};

		this.initCreative();
		this.$updateCreative = _.debounce(this.updateCreative, 1000, {
			'leading': false,
			'trailing': true
		});
		this.$updateCreativeJson = _.debounce(this.updateCreativeJson, 1000, {
			'leading': false,
			'trailing': true
		});
		this.$Data.getAzureSettings();

		this.$eventsBus.$on('custom-voice-active', this.voiceActivated);
		this.$eventsBus.$on('update-iab-categories', this.openIabCategoriesDialog);
		this.$eventsBus.$on('signalr-on-notification', this.signalrOnNotification);

		if (!this.opts) {
			this.$auth.getGlobalProcessId('creative');
		}
	},
	deactivated() {
		this.$setTitle()
	},
	destroyed() {
		delete window.creative;
		delete this.$totalDefaultVoiceReplacedLast;
		this.removeCreativeLock();

		if (this.$checkCreativeEditingLockTimer) {
			clearTimeout(this.$checkCreativeEditingLockTimer);
			delete this.$checkCreativeEditingLockTimer;
		}

		this.$eventsBus.$off('custom-voice-active', this.voiceActivated);
		this.$eventsBus.$off('update-iab-categories', this.openIabCategoriesDialog);
		this.$eventsBus.$off('signalr-on-notification', this.signalrOnNotification);
	},
	beforeRouteLeave(to, from, next) {
		if (!this.creativeIsBusy || !this.dataIsLoadedFirst || this.$auth.isPublishMode()) next();
		else {
			next(false);
			this.$notifyError(this.$tCaption('Creative is busy, please wait!'));
		}
	},
	methods: {
		gotoBackLink() {
			this.$router.push(this.opts.backLink.route);
		},
		downloadBrief() {
			EasyCreativeHelper.getFormedResultsRTF(this.creative?.JsonConfig?.brief?.results, true);
		},
		openServicesManager() {
			this.$refs.header.$refs.creativeDetail.openServicesManager();
		},
		splitLines(text) {
			return `${text}`.split('\n');
		},
		mergeDestPropsOnly(dest, source) {
			for (let key in dest) {
				if (source?.hasOwnProperty(key)) {
					if (!dest[key] || !(typeof (dest[key]) === 'object'))
						dest[key] = source[key];
					else {
						if (Array.isArray(dest[key])) {
							dest[key] = source[key];
						} else {
							this.mergeDestPropsOnly(dest[key], source[key]);
						}
					}
				}
			}

			return dest;
		},
		openIabCategoriesDialog() {
			this.$refs.creativeIabCategories.openIabCategoriesDialog();
		},
		sendValidationRequests(sendOnlyUndelivered = false) {
			this.sendingValidationRequest = true;
			return this.$Data
				.getExternalApi(
					'Creative/SendValidationRequests',
					`creativeId=${this.creativeId}&sendOnlyUndelivered=${sendOnlyUndelivered}`
				)
				.then(() => {
					this.getCreativeValidationStatistic();
					this.$notifyMessage(this.$tCaption('Validation emails has been sent'));
				})
				.finally(() => {
					this.sendingValidationRequest = false;
				});
		},
		getCreativeValidationStatistic() {
			return this.$Data
				.getExternalApi(
					'Creative/GetCreativeValidationStatistic',
					`creativeId=${this.creativeId}`
				)
				.then((r) => {
					if (__DEBUG__) console.log('GetCreativeValidationStatistic', r);
					this.validationDueDate = r.ValidationDueDate;
					this.validationStatus = r.ValidationStatus;
				});
		},
		signalrOnNotification({ eventName, data }) {
			if (eventName === 'CreativeValidationRequestsSent') {
				this.getCreativeValidationStatistic(true);
			}
			if (data?.Id === this.creativeId || data?.CreativeId === this.creativeId) {
				this.checkPublicationStatisticProgress(true);
			}
			if (eventName === 'CreativeGenerationStatusChanged' && data?.Id === this.creativeId) {
				if (!this.creative || this.isGenerating) {
					this.initCreative();
				}
			}
			if (eventName === 'PersistentJobEntity.StatusChanged' && data?.Code === 'GenerationInsertions') {
				if (data?.ResultJson && data?.Status > 2) {
					this.$eventsBus?.$emit('notification-busy', false);

					const json = JSON.parse(data.ResultJson)?.Data;
					if (__DEBUG__)
						console.debug('PersistentJobEntity.StatusChanged', { json });
					(json.ImportedInsertions || []).map(f => {
						const track = this.allTracks.find(t => t.id === f.InsertionId);
						if (__DEBUG__)
							console.debug('PersistentJobEntity.StatusChanged track', { track });
						if (track) {
							if (f.Error) {
								this.$set(track.insertion.JsonConfig, 'subTracksGenerationLogUrl', f?.Url);
							} else {
								this.$set(track.insertion.JsonConfig, 'subTracksGenerationLogUrl', null);
							}
							track.insertion.LastProcessingJob = data;
							
							track.__loadSubTracks(true);
							track.__isGeneratingSubInsertions = false;
							track.__isLoading--;
						}
					});
				}
			}
		},
	},
};